import { circularDifference } from '@/util/math'
import type { ReadonlyVector2, Vector2 } from '@/util/Vector2'

/**
 * Computes the smallest signed difference between two angles in radians.
 * The result is in the range (-π, π].
 *
 * @param a - The target angle in radians
 * @param b - The reference angle in radians
 * @returns The signed difference from b to a, in radians
 */
export function angleDifference(a: number, b: number): number {
    return circularDifference(a, b, Math.PI * 2)
}

/**
 * Gets the center of a DOMRect.
 * @param rect
 * @param out Sets the x,y on the out Vector
 */
export function getRectCenter(rect: DOMRectReadOnly, out: Vector2) {
    out.set(rect.left + rect.width / 2, rect.top + rect.height / 2)
}

/**
 * Returns the taxicab distance between two DOMRect objects.
 */
export function edgeManhattanDistanceBetweenRects(a: DOMRectReadOnly, b: DOMRectReadOnly): number {
    const dx = Math.max(0, b.left - a.right, a.left - b.right)
    const dy = Math.max(0, b.top - a.bottom, a.top - b.bottom)
    return dx + dy
}

/**
 * Checks whether a 2D ray intersects a given DOMRect.
 *
 * The ray is defined by an origin point and a direction vector.
 * The DOMRect is treated as an axis-aligned bounding box (AABB).
 *
 * @param origin - The starting point of the ray (in screen coordinates).
 * @param direction - The direction vector of the ray. Does not need to be normalized.
 * @param rect - The DOMRect to test for intersection.
 * @returns True if the ray intersects the rectangle; otherwise, false.
 */
export function rayIntersectsRect(
    origin: ReadonlyVector2,
    direction: ReadonlyVector2,
    rect: DOMRectReadOnly
): boolean {
    const invDirX = 1 / direction.x
    const invDirY = 1 / direction.y

    const tx1 = (rect.left - origin.x) * invDirX
    const tx2 = (rect.right - origin.x) * invDirX

    const ty1 = (rect.top - origin.y) * invDirY
    const ty2 = (rect.bottom - origin.y) * invDirY

    const tMin = Math.max(Math.min(tx1, tx2), Math.min(ty1, ty2))
    const tMax = Math.min(Math.max(tx1, tx2), Math.max(ty1, ty2))

    return tMax >= 0 && tMin <= tMax
}

/**
 * Returns the intersection of two DOMRects. If there is no overlap,
 * returns null.
 *
 * @param a - The first DOMRect.
 * @param b - The second DOMRect.
 * @returns A new DOMRect representing the overlapping area, or null if none.
 */
export function intersectRects(a: DOMRect, b: DOMRect): DOMRect | null {
    const left = Math.max(a.left, b.left)
    const top = Math.max(a.top, b.top)
    const right = Math.min(a.right, b.right)
    const bottom = Math.min(a.bottom, b.bottom)

    if (right <= left || bottom <= top) return null

    return {
        left,
        top,
        right,
        bottom,
        width: right - left,
        height: bottom - top,
        x: left,
        y: top,
        toJSON: () => null,
    }
}
